import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Play, Square, CalendarDays, CalendarOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, differenceInBusinessDays } from "date-fns";
import { calculateOvertimePay } from "@/lib/overtime";

const TimeClock = () => {
  const { user } = useAuth();
  const [employee, setEmployee] = useState<any>(null);
  const [activeEntry, setActiveEntry] = useState<any>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<any[]>([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsed, setElapsed] = useState("");
  const [showTimeOffDialog, setShowTimeOffDialog] = useState(false);
  const [toForm, setToForm] = useState({ request_type: "vacation", start_date: "", end_date: "", reason: "" });

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  useEffect(() => {
    if (!activeEntry) { setElapsed(""); return; }
    const tick = () => {
      const diff = (Date.now() - new Date(activeEntry.clock_in).getTime()) / 1000;
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = Math.floor(diff % 60);
      setElapsed(`${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeEntry]);

  const fetchData = async () => {
    setLoading(true);
    const { data: emp } = await supabase
      .from("employees")
      .select("*")
      .eq("user_id", user!.id)
      .maybeSingle();

    if (!emp) { setLoading(false); return; }
    setEmployee(emp);

    const [{ data: active }, { data: recent }, { data: tos }] = await Promise.all([
      supabase.from("time_entries").select("*").eq("employee_id", emp.id).is("clock_out", null).order("clock_in", { ascending: false }).limit(1).maybeSingle(),
      supabase.from("time_entries").select("*").eq("employee_id", emp.id).not("clock_out", "is", null).order("clock_in", { ascending: false }).limit(20),
      supabase.from("time_off_requests").select("*").eq("employee_id", emp.id).order("created_at", { ascending: false }).limit(20),
    ]);
    setActiveEntry(active);
    setEntries(recent || []);
    setTimeOffRequests(tos || []);
    setLoading(false);
  };

  const clockIn = async () => {
    if (!employee) return;
    setClockLoading(true);
    const { error } = await supabase.from("time_entries").insert({
      employee_id: employee.id,
      clock_in: new Date().toISOString(),
      notes: notes || null,
    });
    if (error) toast.error("Failed to clock in");
    else { toast.success("Clocked in!"); setNotes(""); await fetchData(); }
    setClockLoading(false);
  };

  const clockOut = async () => {
    if (!activeEntry) return;
    setClockLoading(true);
    const { error } = await supabase
      .from("time_entries")
      .update({ clock_out: new Date().toISOString(), notes: notes || activeEntry.notes })
      .eq("id", activeEntry.id);
    if (error) toast.error("Failed to clock out");
    else { toast.success("Clocked out!"); setNotes(""); await fetchData(); }
    setClockLoading(false);
  };

  const submitTimeOff = async () => {
    if (!toForm.start_date || !toForm.end_date) { toast.error("Select start and end dates"); return; }
    const days = differenceInBusinessDays(new Date(toForm.end_date), new Date(toForm.start_date)) + 1;
    if (days <= 0) { toast.error("End date must be after start date"); return; }
    const { error } = await supabase.from("time_off_requests").insert({
      employee_id: employee.id,
      request_type: toForm.request_type,
      start_date: toForm.start_date,
      end_date: toForm.end_date,
      total_days: days,
      reason: toForm.reason || null,
    });
    if (error) toast.error("Failed to submit: " + error.message);
    else { toast.success("Time off request submitted"); setShowTimeOffDialog(false); setToForm({ request_type: "vacation", start_date: "", end_date: "", reason: "" }); fetchData(); }
  };

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);

  const todayHours = entries
    .filter(e => format(new Date(e.clock_in), "yyyy-MM-dd") === format(now, "yyyy-MM-dd"))
    .reduce((sum, e) => sum + (e.total_hours || 0), 0);

  const weeklyHours = entries
    .filter(e => { const d = new Date(e.clock_in); return d >= weekStart && d <= weekEnd; })
    .reduce((sum, e) => sum + (e.total_hours || 0), 0);

  const monthlyHours = entries
    .filter(e => { const d = new Date(e.clock_in); return d >= monthStart && d <= monthEnd; })
    .reduce((sum, e) => sum + (e.total_hours || 0), 0);

  const overtime = employee ? calculateOvertimePay(weeklyHours, employee.hourly_rate) : null;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Clock className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!employee) {
    return (
      <AppLayout>
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Time Clock</h1>
          <p className="text-muted-foreground">You are not registered as an employee. Contact your administrator to link your account.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-foreground">Time Clock</h1>

        {/* Clock In/Out Card */}
        <Card className="border-primary/20">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Welcome, {employee.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{employee.role} · ${employee.hourly_rate}/hr</p>
              </div>
              {activeEntry ? (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Currently clocked in</p>
                  <p className="text-xs text-muted-foreground">Since {format(new Date(activeEntry.clock_in), "h:mm:ss a")}</p>
                  <p className="text-2xl font-mono font-bold text-primary">{elapsed}</p>
                </div>
              ) : null}
            </div>
            <Textarea placeholder="Add notes (optional)..." value={notes} onChange={(e) => setNotes(e.target.value)} className="resize-none" rows={2} />
            <div className="flex gap-3">
              {!activeEntry ? (
                <Button onClick={clockIn} disabled={clockLoading} className="flex-1 gap-2" size="lg">
                  <Play className="h-5 w-5" /> Clock In
                </Button>
              ) : (
                <Button onClick={clockOut} disabled={clockLoading} variant="destructive" className="flex-1 gap-2" size="lg">
                  <Square className="h-5 w-5" /> Clock Out
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Today</p>
              <p className="text-xl font-bold text-foreground">{todayHours.toFixed(1)}h</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">This Week</p>
              <p className="text-xl font-bold text-foreground">{weeklyHours.toFixed(1)}h</p>
              {overtime && overtime.overtimeHours > 0 && (
                <p className="text-xs text-orange-500 font-medium">{overtime.overtimeHours.toFixed(1)}h OT</p>
              )}
              <p className="text-xs text-muted-foreground">${overtime?.totalPay.toFixed(2) ?? "0.00"}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">This Month</p>
              <p className="text-xl font-bold text-foreground">{monthlyHours.toFixed(1)}h</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground">Pay Rate</p>
              <p className="text-xl font-bold text-foreground">${employee.hourly_rate}</p>
              <p className="text-xs text-muted-foreground">/hour (1.5x OT)</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="entries">
          <TabsList className="w-full flex">
            <TabsTrigger value="entries" className="flex-1">Recent Entries</TabsTrigger>
            <TabsTrigger value="timeoff" className="flex-1">Time Off</TabsTrigger>
          </TabsList>

          <TabsContent value="entries" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="h-5 w-5" /> Recent Entries
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>In</TableHead>
                        <TableHead>Out</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Pay</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No entries yet</TableCell></TableRow>
                      )}
                      {entries.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell className="whitespace-nowrap">{format(new Date(e.clock_in), "EEE, MMM d, yyyy")}</TableCell>
                          <TableCell className="whitespace-nowrap">{format(new Date(e.clock_in), "h:mm:ss a")}</TableCell>
                          <TableCell className="whitespace-nowrap">{e.clock_out ? format(new Date(e.clock_out), "h:mm:ss a") : "—"}</TableCell>
                          <TableCell>{e.total_hours?.toFixed(2) || "—"}</TableCell>
                          <TableCell>${e.total_pay?.toFixed(2) || "—"}</TableCell>
                          <TableCell>
                            <Badge variant={e.status === "approved" ? "default" : e.status === "rejected" ? "destructive" : "secondary"} className="text-xs">
                              {e.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="timeoff" className="mt-4 space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowTimeOffDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Request Time Off
              </Button>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarOff className="h-5 w-5" /> My Time Off Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Start</TableHead>
                        <TableHead>End</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeOffRequests.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No time off requests</TableCell></TableRow>
                      )}
                      {timeOffRequests.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="capitalize">{r.request_type}</TableCell>
                          <TableCell className="whitespace-nowrap">{format(new Date(r.start_date), "MMM d, yyyy")}</TableCell>
                          <TableCell className="whitespace-nowrap">{format(new Date(r.end_date), "MMM d, yyyy")}</TableCell>
                          <TableCell>{r.total_days}</TableCell>
                          <TableCell>
                            <Badge variant={r.status === "approved" ? "default" : r.status === "rejected" ? "destructive" : "secondary"} className="text-xs capitalize">
                              {r.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{format(new Date(r.created_at), "MMM d, yyyy")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Time Off Request Dialog */}
        <Dialog open={showTimeOffDialog} onOpenChange={setShowTimeOffDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Request Time Off</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={toForm.request_type} onValueChange={v => setToForm({ ...toForm, request_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacation">Vacation</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={toForm.start_date} onChange={e => setToForm({ ...toForm, start_date: e.target.value })} /></div>
                <div><Label>End Date</Label><Input type="date" value={toForm.end_date} onChange={e => setToForm({ ...toForm, end_date: e.target.value })} /></div>
              </div>
              <div><Label>Reason (optional)</Label><Textarea value={toForm.reason} onChange={e => setToForm({ ...toForm, reason: e.target.value })} rows={3} /></div>
            </div>
            <DialogFooter><Button onClick={submitTimeOff}>Submit Request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default TimeClock;
